import { createFileRoute } from '@tanstack/react-router'

import { useState, useEffect } from "react";
import { useSession } from '@/lib/mocks/session';
import { useRouter, useParams } from '@/lib/mocks/router';
import { motion } from "framer-motion";
import { Button } from '@platform/ui';
import { AlertCircle } from "lucide-react";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  TrendingUp,
  Clock,
  FileText,
  Download,
  ExternalLink,
  Shield,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Target,
  Calendar,
  Percent,
  BarChart3,
  Info,
  Star,
  Zap,
  Share2,
  Heart,
  Play,
  Image as ImageIcon,
  File,
  Grid,
  CreditCard,
  Wallet,
  Loader2,
} from "lucide-react";
import InvestmentCalculator from '@/modules/invest/InvestmentCalculator';
import { registrar } from '@/lib/demo/telemetry';
import { paso } from '@/modules/demo/DemoTour';


import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@platform/ui';
import { Badge } from '@platform/ui';
import { Progress } from '@platform/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@platform/ui';
import { Input } from '@platform/ui';
import { Separator } from '@platform/ui';
import FundWalletModal from '@/modules/wallet/FundWalletModal';
import { OnboardingRequiredModal } from '@/modules/onboarding/OnboardingRequiredModal';
import { useOnboarding } from '@/lib/onboarding/useOnboarding';
import { useProjectDetail, ProjectDetail } from '@/lib/mocks/useProjects';
import TransactionStatusModal from '@/modules/onramp/TransactionStatusModal';
import { OperationAgreementModal } from '@/modules/invest/OperationAgreementModal';
import { useTranslations } from '@/i18n/useTranslations';
import { brand, features, blockchain, platform } from '@/lib/platform';
import { BrandLogo } from '@/modules/shared/BrandLogo';
import { useCurrentUser, useSendUserOperation } from '@/lib/mocks/cdp';
import { encodeFunctionData, parseUnits, createPublicClient, http, formatUnits } from "viem";
import { baseSepolia } from "viem/chains";
import { rwaAbi, tokenAbi } from "@platform/abis";
import { simulateDemoCheckout, STEP_LABELS, type DemoCheckoutStep } from '@/lib/demo-checkout';
import { demoActions, useDemoState } from '@/lib/demo-store';


// Hardcoded for now or move to config/contracts.ts
const PAYMENT_TOKEN_ADDRESS = "0x95d833C267810192daEf7B443394c2537E988C5a"; // Custom USDC for Demo

// Acuerdos de operación ya firmados — persistido en localStorage para que el
// contrato sólo se muestre la PRIMERA vez por proyecto. Una vez firmado, las
// siguientes compras del mismo proyecto saltan directo a la acción.
const SIGNED_AGREEMENTS_KEY = 'platform.signed-agreements.v1';

function hasSignedAgreement(projectId: string): boolean {
  if (typeof window === 'undefined' || !projectId) return false;
  try {
    const raw = window.localStorage.getItem(SIGNED_AGREEMENTS_KEY);
    if (!raw) return false;
    const ids = JSON.parse(raw);
    return Array.isArray(ids) && ids.includes(projectId);
  } catch {
    return false;
  }
}

function markAgreementSigned(projectId: string): void {
  if (typeof window === 'undefined' || !projectId) return;
  try {
    const raw = window.localStorage.getItem(SIGNED_AGREEMENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const ids = new Set<string>(Array.isArray(parsed) ? parsed : []);
    ids.add(projectId);
    window.localStorage.setItem(SIGNED_AGREEMENTS_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore quota errors */
  }
}

function ProjectDetailPage() {
  const { data: session } = useSession();
  const router = useRouter();
  const params = useParams();
  const projectId = params.id as string;
  const t = useTranslations("ProjectDetail");
  
  // CDP Smart Wallet Hooks
  const { currentUser } = useCurrentUser();
  const { sendUserOperation, status: opStatus } = useSendUserOperation();
  
  // Fetch project from BigAdmin API
  const { project, isLoading, error, refetch } = useProjectDetail(projectId);
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [investmentAmount, setInvestmentAmount] = useState(100);
  const [confirmedInvestmentAmount, setConfirmedInvestmentAmount] = useState(100);
  const [isFavorite, setIsFavorite] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [showFundModal, setShowFundModal] = useState(false);
  const [userWalletAddress, setUserWalletAddress] = useState<string | null>(null);
  // Initialize KYC from the demo-store synchronously so the first render
  // already reflects a previous verification — no flicker, no false-positive
  // KycRequiredModal flashing when the user re-opens the page already verified.
  const [kycStatus, setKycStatus] = useState<string>(() => {
    if (features.demoMode) {
      // Read directly via the persisted demo-store snapshot (localStorage-backed).
      try {
        const raw = typeof window !== 'undefined'
          ? window.localStorage.getItem('platform.demo-state.v1')
          : null;
        if (raw) {
          const parsed = JSON.parse(raw);
          return parsed?.user?.kycStatus ?? 'PENDING';
        }
      } catch { /* ignore */ }
    }
    return 'PENDING';
  });
  const [showKycModal, setShowKycModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<"fund" | "wallet" | null>(null);
  const [transactionState, setTransactionState] = useState<{
    status: "idle" | "processing" | "success" | "error";
    txHash: string | null;
    error: string | null;
    step?: DemoCheckoutStep;
  }>({ status: "idle", txHash: null, error: null });

  // Update investment amount when project loads
  useEffect(() => {
    if (project?.minInvestment) {
      setInvestmentAmount(project.minInvestment);
    }
  }, [project]);

  // Telemetría: abrir una ficha es la primera señal de interés concreto — deja
  // de mirar el catálogo y elige un activo. Se emite cuando el proyecto ya
  // cargó, no al montar: si la carga falla, no hubo ficha que ver.
  // La deduplicación es por proyecto y por día, así que volver a la misma ficha
  // en la misma jornada no suma de nuevo.
  useEffect(() => {
    if (!project?.id) return;
    registrar('project_viewed', { projectId: project.id, name: project.name });
  }, [project?.id, project?.name]);

  // Subscribe to the demo store so simulated KYC/wallet/balance flow into this
  // page the moment they change, without a reload or backend round-trip.
  const demoState = useDemoState();

  // Fetch user wallet address and KYC
  useEffect(() => {
    if (!session?.user) return;

    // Demo mode: read from the demo-store (kyc + wallet are persisted there
    // by the simulator). Skips the backend entirely.
    if (features.demoMode) {
      setKycStatus(demoState.user?.kycStatus ?? 'PENDING');
      if (demoState.walletAddress) setUserWalletAddress(demoState.walletAddress);
      return;
    }

    // Wallet Sync
    fetch('/api/wallet/sync')
      .then((res) => res.json())
      .then((data) => {
        if (data.walletAddress) {
          setUserWalletAddress(data.walletAddress);
        }
      })
      .catch(console.error);

    // KYC Check
    fetch('/api/kyc/check-status')
      .then((res) => res.json())
      .then((data) => {
        setKycStatus(data.kycStatus || 'PENDING');
      })
      .catch(console.error);
  }, [session, demoState.user?.kycStatus, demoState.walletAddress]);

  // Live KYC gate — in demo mode, derive from the demo-store snapshot so the
  // value is always in sync with the simulator (no race with the local state
  // update that previously caused the KycRequiredModal to flash intermittently
  // when opening Fund Wallet on an already-verified user).
  const isKycVerified = features.demoMode
    ? demoState.user?.kycStatus === 'VERIFIED'
    : kycStatus === 'VERIFIED';

  // Onboarding de 2 tareas (KYC + preguntas de seguridad). Invertir exige AMBAS.
  const { isComplete: isOnboardingComplete } = useOnboarding();

  if (userWalletAddress) {
      // No-op to avoid unused variable warning until properly used
  }

  // Auto-connect CDP Wallet logic
  /* 
   * This logic ensures the CDP wallet is authenticated and connected 
   * as soon as the session is available. This mimics the example logic provided.
   */
  // Note: authenticateWithJWT is removed because CDPHooksProvider handles it automatically via customAuth.getJwt callback.
  // We just need to ensure we use valid hooks.

  
  const handleInvestWithSmartWallet = async (bypassAgreement = false) => {
    if (!isOnboardingComplete) {
      setShowKycModal(true);
      return;
    }
    if (!project) {
      setTransactionState({ status: 'error', txHash: null, error: 'Proyecto no cargado' });
      return;
    }

    if (features.demoMode && !bypassAgreement && !hasSignedAgreement(projectId)) {
      setPendingAction("wallet");
      setShowAgreementModal(true);
      return;
    }

    // ============================================================================
    // DEMO CHECKOUT — end-to-end simulation (Stripe + on-chain mint + receipt).
    // Toggled via `features.demoCheckout` in platform.config.json. When on we skip
    // the real CDP `sendUserOperation` and emit a synthetic-but-valid tx hash so
    // the rest of the UI (status modal, explorer link, portfolio) works as if a
    // real transaction had landed on Base Sepolia.
    // ============================================================================
    if (features.demoCheckout) {
      const currentInvestmentAmount = investmentAmount;
      setConfirmedInvestmentAmount(currentInvestmentAmount);
      setTransactionState({ status: "processing", txHash: null, error: null, step: 'stripe-authorizing' });

      try {
        const result = await simulateDemoCheckout({
          network: blockchain.chainName,
          explorerBaseUrl: `${blockchain.blockExplorer.replace(/\/$/, '')}/tx/`,
          onStep: (step) =>
            setTransactionState((prev) => ({ ...prev, step })),
        });

        const tokensBought =
          project.tokenPrice && project.tokenPrice > 0
            ? currentInvestmentAmount / project.tokenPrice
            : 0;

        // Persist on the in-memory demo store so the portfolio + history pick it up.
        demoActions.addTransaction({
          type: 'investment',
          amountUsd: currentInvestmentAmount,
          tokenSymbol: project.tokenDetails?.symbol || 'RWA',
          tokenAmount: tokensBought,
          projectName: project.name,
          status: 'confirmed',
        });

        // Comprobante por correo: el backend (Better Auth + Resend) lo manda al
        // usuario logueado. Best-effort — un fallo no rompe la UX del demo.
        try {
          const apiBase = (platform.domain?.adminApiUrl ?? '').replace(/\/$/, '');
          await fetch(`${apiBase}/purchase/receipt`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projectName: project.name,
              tokenAmount: tokensBought,
              tokenSymbol: project.tokenDetails?.symbol || 'RWA',
              amountUsd: currentInvestmentAmount,
              txHash: result.txHash,
            }),
          });
        } catch {
          /* offline / no backend in demo mode — ignore */
        }

        setTransactionState({
          status: 'success',
          txHash: result.txHash,
          error: null,
          step: 'done',
        });

        // Telemetría: la compra completada es el segundo evento más pesado del
        // catálogo (40). Va aquí y no en el clic de "invertir" a propósito —
        // abrir el flujo y abandonarlo a mitad no es lo mismo que terminarlo, y
        // esa diferencia es exactamente lo que el puntaje tiene que capturar.
        registrar('purchase_simulated', {
          projectId: project.id,
          amountUsd: currentInvestmentAmount,
        });

        setTimeout(() => refetch(), 1500);
        return;
      } catch (err: any) {
        setTransactionState({
          status: 'error',
          txHash: null,
          error: err?.message || 'Error en la simulación del checkout',
        });
        return;
      }
    }

    const smartAccount = currentUser?.evmSmartAccounts?.[0];
    console.log("Smart Account:", smartAccount);
    //if (!smartAccount || !project?.tokenDetails?.contractAddress) return;

    // 1. Definir monto (usando investmentAmount)
    const currentInvestmentAmount = investmentAmount;
    setConfirmedInvestmentAmount(currentInvestmentAmount);
    
    // Asumimos 6 decimales para USDC/USDT
    const amount = parseUnits(currentInvestmentAmount.toString(), 6); 
    
    setTransactionState({ status: "processing", txHash: null, error: null });

    // DEBUG: Leer variable 'usdt' del contrato RWA
    try {
      const client = createPublicClient({
        chain: baseSepolia,
        transport: http(),
      });

      console.log(project?.tokenDetails.contractAddress, "2")

      const rwaUsdt = await client.readContract({
        address: project?.tokenDetails.contractAddress as `0x${string}`,
        abi: rwaAbi,
        functionName: "usdt",
      });

      const rwaTokensForSale = await client.readContract({
        address: project?.tokenDetails.contractAddress as `0x${string}`,
        abi: rwaAbi,
        functionName: "tokensForSale",
      });

      const rwaPriceUSD = await client.readContract({
        address: project?.tokenDetails.contractAddress as `0x${string}`,
        abi: rwaAbi,
        functionName: "tokenPriceUSD",
      });
      
      console.log("🔍 RWA Contract USDT Address:", rwaUsdt);
      console.log("🔍 RWA Tokens For Sale:", formatUnits(rwaTokensForSale as bigint, 18)); // Assuming 18 decimals for RWA Token
      console.log("🔍 RWA Token Price USD:", formatUnits(rwaPriceUSD as bigint, 6)); // Assuming 6 decimals for USD Price
    } catch (error) {
      console.error("❌ Error reading USDT from RWA contract:", error);
    }

    // 2. Codificar llamada 1: Aprobar USDC al Proyecto
    const approveData = encodeFunctionData({
      abi: tokenAbi,
      functionName: "approve",
      args: [project.tokenDetails.contractAddress as `0x${string}`, amount]
    });

    // 3. Codificar llamada 2: Comprar Tokens (Invest)
    const investData = encodeFunctionData({
        abi: rwaAbi,
        functionName: "buyTokensUsd",
        args: [amount, smartAccount as `0x${string}`] // _to es la propia smart wallet
    });

    try {
      console.log("Sending UserOperation...");
      // 4. Enviar User Operation (Batch)
      const { userOperationHash } = await sendUserOperation({
        evmSmartAccount: smartAccount,
        network: "base-sepolia", // Asumiendo Base Sepolia por los logs
        calls: [
          {
            to: PAYMENT_TOKEN_ADDRESS as `0x${string}`, // Dirección del Token de Pago
            data: approveData,
            value: 0n
          },
          {
            to: project?.tokenDetails.contractAddress as `0x${string}`, // Dirección del Proyecto RWA
            data: investData,
            value: 0n
          }
        ],
        useCdpPaymaster: true // Gasless!
      });

      console.log("Inversión enviada:", userOperationHash);
      
      // Registrar la inversión en el Backend (BigAdmin)
      try {
        const tokensBought = project.tokenPrice && project.tokenPrice > 0 
          ? currentInvestmentAmount / project.tokenPrice 
          : 0;

        const payload = {
          projectId: project.id,
          walletAddress: smartAccount,
          amountTokens: tokensBought.toString(),
          amountUsd: currentInvestmentAmount.toString(),
          txHash: userOperationHash,
          tokenPrice: project.tokenPrice?.toString() || "0",
          email: session?.user?.email || "",
          name: session?.user?.name || ""
        };

        console.log("Registrando inversión en backend:", payload);

        await fetch('/api/invest', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        
        console.log("Inversión registrada correctamente en backend");
      } catch (backendError) {
        console.error("Error al registrar inversión en backend:", backendError);
        // No bloqueamos el flujo de éxito de la UI aunque falle el registro backend,
        // pero idealmente deberíamos notificar o reintentar.
      }

      setTransactionState({ status: "success", txHash: userOperationHash, error: null });
      
      // Actualizar datos del proyecto (progreso, inversores, etc.)
      setTimeout(() => {
        refetch();
      }, 2000); // Pequeño delay para asegurar que el backend haya procesado la tx
    } catch (error: any) {
      console.error("Error al invertir con Smart Wallet:", error);
      setTransactionState({
        status: "error",
        txHash: null,
        error: error.message || "Error desconocido al procesar la transacción",
      });
    }
  };

  const handleInvestClick = () => {
    if (!isOnboardingComplete) {
      setShowKycModal(true);
    } else {
      if (features.demoMode && !hasSignedAgreement(projectId)) {
        setPendingAction("fund");
        setShowAgreementModal(true);
      } else {
        setShowFundModal(true);
      }
    }
  };

  const handleAgreementConfirm = () => {
    // Recuerda la firma para este proyecto: el contrato no volverá a aparecer.
    markAgreementSigned(projectId);
    setShowAgreementModal(false);
    if (pendingAction === "fund") {
      setShowFundModal(true);
    } else if (pendingAction === "wallet") {
      void handleInvestWithSmartWallet(true);
    }
    setPendingAction(null);
  };

  // Loading state
  if (isLoading && !project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h2 className="text-xl font-semibold">{t("not_found")}</h2>
          <p className="text-muted-foreground">{error || t("not_available")}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => router.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("back")}
            </Button>
            <Button onClick={() => refetch()}>
              {t("retry")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const progressPercentage = project.raisedAmount != null && project.targetAmount != null && project.targetAmount > 0
    ? (project.raisedAmount / project.targetAmount) * 100
    : 0;
  const tokensFromInvestment = project.tokenPrice != null && project.tokenPrice > 0
    ? investmentAmount / project.tokenPrice
    : 0;

  const nextImage = () => {
    if (project.images && project.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % project.images.length);
    }
  };

  const prevImage = () => {
    if (project.images && project.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + project.images.length) % project.images.length);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-3 gap-1"><Zap className="w-3 h-3" /> {t("status_active")}</Badge>;
      case "funded":
        return <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30 px-3 gap-1"><CheckCircle2 className="w-3 h-3" /> {t("status_funded")}</Badge>;
      case "coming_soon":
        return <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 px-3 gap-1"><Clock className="w-3 h-3" /> {t("status_coming_soon")}</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-transparent">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                className="gap-2"
                onClick={() => router.back()}
              >
                <ArrowLeft className="w-4 h-4" />
                {t("back")}
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <BrandLogo variant="primary" textClassName="text-base font-semibold tracking-tight" />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsFavorite(!isFavorite)}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? "fill-red-500 text-red-500" : ""}`} />
              </Button>
              <Button variant="ghost" size="icon">
                <Share2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Column - Gallery (Sticky) */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 space-y-6">
              {/* Image Gallery */}
              <Card className="border-border/40 overflow-hidden">
                <div className="relative aspect-[4/3] bg-muted overflow-hidden">
                  <motion.div
                    key={currentImageIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: project.images && project.images.length > 0 
                        ? `url(${project.images[currentImageIndex]})` 
                        : 'none',
                      backgroundColor: 'hsl(var(--muted))',
                    }}
                  />
                  
                  {/* Navigation Arrows */}
                  {project.images && project.images.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/70 backdrop-blur-sm hover:bg-background/90 h-10 w-10"
                        onClick={prevImage}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/70 backdrop-blur-sm hover:bg-background/90 h-10 w-10"
                        onClick={nextImage}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </>
                  )}

                  {/* Image Count */}
                  {project.images && project.images.length > 0 && (
                    <div className="absolute top-3 right-3 bg-background/70 backdrop-blur-sm rounded-full px-3 py-1 text-sm flex items-center gap-1.5">
                      <Grid className="w-4 h-4" />
                      {currentImageIndex + 1}/{project.images.length}
                    </div>
                  )}
                </div>

                {/* Thumbnail Strip */}
                {project.images && project.images.length > 1 && (
                  <CardContent className="p-3">
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {project.images.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                            index === currentImageIndex
                              ? "border-primary ring-2 ring-primary/20"
                              : "border-transparent opacity-60 hover:opacity-100"
                          }`}
                        >
                          <div
                            className="w-full h-full bg-cover bg-center"
                            style={{ backgroundImage: `url(${img})` }}
                          />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Investment Card */}
              <Card className="border-border/40 overflow-hidden">
                <div className="bg-gradient-to-r from-primary/10 to-violet-500/10 p-6">
                  <div className="space-y-4">
                    {/* Progress */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t("funding_progress")}</span>
                        <span className="font-semibold">{progressPercentage.toFixed(0)}%</span>
                      </div>
                      <Progress value={progressPercentage} className="h-3" />
                      <div className="flex justify-between text-sm">
                        <span className="font-semibold">${project.raisedAmount != null ? project.raisedAmount.toLocaleString() : "N/A"}</span>
                        <span className="text-muted-foreground">de ${project.targetAmount != null ? project.targetAmount.toLocaleString() : "N/A"}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 rounded-xl bg-background/50">
                        <p className="text-2xl font-bold text-primary">${project.tokenPrice}</p>
                        <p className="text-xs text-muted-foreground">{t("per_token")}</p>
                      </div>
                      <div className="text-center p-3 rounded-xl bg-background/50">
                        <p className="text-2xl font-bold text-emerald-500">{project.expectedReturn}</p>
                        <p className="text-xs text-muted-foreground">{t("return")}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <CardContent className="p-6 space-y-5" {...paso('invertir')}>
                  {/* Investment Input */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">{t("amount_to_invest")}</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="number"
                        value={investmentAmount}
                        onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                        min={project.minInvestment}
                        step={project.tokenPrice}
                        className="pl-10 h-12 text-lg"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("minimum")}: ${project.minInvestment} • {t("you_will_receive")} <strong>{tokensFromInvestment.toFixed(0)}</strong> {t("tokens")} {project.tokenDetails?.symbol || ""}
                    </p>
                  </div>

                  {/* Quick Amounts */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[100, 500, 1000, 5000].map((amount) => (
                      <Button
                        key={amount}
                        variant={investmentAmount === amount ? "default" : "outline"}
                        size="sm"
                        onClick={() => setInvestmentAmount(amount)}
                        className="text-xs"
                      >
                        ${amount}
                      </Button>
                    ))}
                  </div>

                  {/* Investment Options */}
                  {/* Investment Options */}
                  {project.isSaleActive === false ? (
                                          <Button
                        className="w-full h-12 text-base gap-2"
                        onClick={handleInvestClick}
                      >
                        <CreditCard className="w-5 h-5" />
                        {t("fund_account")} (${investmentAmount.toLocaleString()})
                      </Button>
                    // <Button
                    //   className="w-full h-12 text-base bg-amber-500/20 text-amber-500 border-amber-500/30 hover:bg-amber-500/30 cursor-not-allowed"
                    //   disabled={true}
                    // >
                    //   <Clock className="w-4 h-4 mr-2" />
                    //   {t("sale_paused") || "Venta Pausada Temp."}

                    // </Button>
                  ) : project.status === "active" ? (
                    <div className="space-y-3">
                      {/* Option 1: Onramp - Fondear cuenta */}
                      <Button
                        className="w-full h-12 text-base gap-2"
                        onClick={handleInvestClick}
                      >
                        <CreditCard className="w-5 h-5" />
                        {t("fund_account")} (${investmentAmount.toLocaleString()})
                      </Button>
                      
                      {/* Option 2: Comprar Token con Wallet (CDP Smart Wallet) */}
                      <Button
                        variant="outline"
                        className="w-full h-12 text-base gap-2"
                        onClick={() => handleInvestWithSmartWallet(false)}
                        // disabled={opStatus === "pending" || !currentUser?.evmSmartAccounts?.[0] || !project.tokenDetails?.contractAddress}
                      >
                        <Wallet className="w-5 h-5" />
                        {opStatus === "pending" ? "Procesando..." : t("buy_with_wallet")}
                      </Button>
                      
                      <p className="text-xs text-muted-foreground text-center">
                        {t("use_card_or_transfer")}
                      </p>
                    </div>
                  ) : project.status === "funded" ? (
                    <Button
                      className="w-full h-12 text-base"
                      disabled={true}
                    >
                      {t("fully_funded")}
                    </Button>
                  ) : (
                    <Button
                      className="w-full h-12 text-base"
                      disabled={true}
                    >
                      {t("coming_soon_available")}
                    </Button>
                  )}

                  {/* Security Badge */}
                  {/* <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Shield className="w-4 h-4 text-emerald-500" />
                    <span>{t("secure_transactions")}</span>
                  </div> */}

                  <Separator />

                  {/* Key Info */}
                  <div className="space-y-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t("min_investment")}</span>
                      <span className="font-semibold">${project.minInvestment}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">{t("investors")}</span>
                      <span className="font-semibold">{project.investorCount.toLocaleString()}</span>
                    </div>
                    {project.daysLeft > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t("round_closes")}</span>
                        <span className="font-semibold text-amber-500">{project.daysLeft} {t("days")}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Column - Project Info */}
          <div className="lg:col-span-3 space-y-8">
            {/* Title & Status */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                {getStatusBadge(project.status)}
                <Badge variant="outline" className="text-sm px-3 py-1">
                  <Building2 className="w-4 h-4 mr-1" />
                  {project.propertyType}
                </Badge>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold">{project.name}</h1>
              
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>{project.address}</span>
              </div>

              <div className="flex flex-wrap gap-4 pt-2">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-primary" />
                  <span><strong>{project.investorCount.toLocaleString()}</strong> {t("investors")}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span><strong>{project.expectedReturn}</strong> {t("expected_return")}</span>
                </div>
                {project.daysLeft > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span><strong>{project.daysLeft}</strong> {t("days_remaining")}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent">
                <TabsTrigger
                  value="overview"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
                >
                  {t("tab_overview")}
                </TabsTrigger>
                <TabsTrigger
                  value="financials"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
                >
                  {t("tab_financials")}
                </TabsTrigger>
                <TabsTrigger
                  value="token"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
                >
                  {t("tab_token")}
                </TabsTrigger>
                <TabsTrigger
                  value="documents"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
                >
                  {t("tab_documents")}
                </TabsTrigger>
                <TabsTrigger
                  value="3d"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3"
                >
                  {t("tab_3d")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6 space-y-8">
                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">{t("about_project")}</h3>
                  <div className="prose prose-invert max-w-none">
                    <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
                      {project.fullDescription}
                    </p>
                  </div>
                </div>

                {/* Highlights */}
                {project.highlights && project.highlights.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">{t("key_features")}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {project.highlights.map((highlight, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 border border-border/40"
                        >
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          <span>{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amenities */}
                {project.amenities && project.amenities.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">{t("amenities")}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {project.amenities.map((amenity, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10"
                        >
                          <Star className="w-5 h-5 text-primary flex-shrink-0" />
                          <span>{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Developer */}
                <Card className="border-border/40">
                  <CardHeader>
                    <CardTitle className="text-base">{t("developer")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-lg">{project.developer?.name || brand.name}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span>{project.developer?.projects || 1} {t("projects")}</span>
                          {(project.developer?.rating || 0) > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                              {project.developer?.rating}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="financials" className="mt-6 space-y-8" {...paso('calculadora')}>
                <InvestmentCalculator
                  rentalYield={parseFloat(String(project.financials?.rentalYield || "0").replace("%", "")) || 0}
                  appreciationRate={parseFloat(String(project.financials?.appreciationTarget || "0").replace("%", "")) || 0}
                  tokenPrice={project.tokenPrice || 50}
                  minInvestment={project.minInvestment || 100}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Card className="border-border/40">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t("projected_roi")}</p>
                          <p className="text-2xl font-bold text-emerald-500">{project.financials?.projectedROI || "N/A"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/40">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-sky-500/10 flex items-center justify-center">
                          <Percent className="w-6 h-6 text-sky-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t("rental_yield")}</p>
                          <p className="text-2xl font-bold">{project.financials?.rentalYield || "N/A"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/40">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                          <BarChart3 className="w-6 h-6 text-violet-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t("appreciation_target")}</p>
                          <p className="text-2xl font-bold">{project.financials?.appreciationTarget || "N/A"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/40">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{t("holding_period")}</p>
                          <p className="text-2xl font-bold">{project.financials?.holdingPeriod || "N/A"}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className="border-border/40 bg-muted/30">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-muted-foreground">
                        <p>{t("financial_disclaimer")}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="token" className="mt-6 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Card className="border-border/40">
                    <CardContent className="p-6 space-y-4">
                      <h4 className="font-semibold text-lg">{t("token_info")}</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">{t("symbol")}</span>
                          <span className="font-mono font-bold text-primary">{project.tokenDetails?.symbol || "N/A"}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">{t("total_supply")}</span>
                          <span className="font-semibold">{project.tokenDetails?.totalSupply?.toLocaleString() || "N/A"}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">{t("tokens_distributed")}</span>
                          <span className="font-semibold">{project.tokenDetails?.distributed?.toLocaleString() || "0"}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">{t("price_per_token")}</span>
                          <span className="font-bold text-lg text-primary">${project.tokenPrice}</span>
                        </div>
                        {project.tokenDetails?.contractAddress && (
                          <>
                            <Separator />
                            <div className="flex justify-between py-2 items-center">
                              <span className="text-muted-foreground">{t("contract_address")}</span>
                              <a 
                                href={`https://sepolia.basescan.org/address/${project.tokenDetails.contractAddress}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-sm text-primary hover:underline flex items-center gap-1"
                              >
                                {project.tokenDetails.contractAddress.slice(0, 6)}...{project.tokenDetails.contractAddress.slice(-4)}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-border/40">
                    <CardContent className="p-6 space-y-4">
                      <h4 className="font-semibold text-lg">{t("distribution_details")}</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">{t("vesting_period")}</span>
                          <span className="font-semibold">{project.tokenDetails?.vestingPeriod || "N/A"}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">{t("dividend_frequency")}</span>
                          <span className="font-semibold">{project.tokenDetails?.dividendFrequency || "N/A"}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">{t("blockchain")}</span>
                          <span className="font-semibold">Polygon</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">{t("standard")}</span>
                          <span className="font-semibold">ERC-20</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="documents" className="mt-6 space-y-4">
                {project.documents && project.documents.length > 0 ? (
                  <div className="grid grid-cols-1 gap-3">
                    {project.documents.map((doc, index) => (
                      <Card
                        key={index}
                        className="border-border/40 hover:border-primary/40 transition-colors cursor-pointer group"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-6 h-6 text-red-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate group-hover:text-primary transition-colors">
                                {doc.name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {doc.type} • {doc.size}
                              </p>
                            </div>
                            <Button variant="ghost" size="icon" className="shrink-0">
                              <Download className="w-5 h-5" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="border-border/40">
                    <CardContent className="p-8 text-center">
                      <File className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {t("documents_coming_soon")}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="3d" className="mt-6">
                <Card className="border-border/40 overflow-hidden bg-card/50 backdrop-blur-sm p-0">
                  <div className="relative w-full aspect-[16/9] max-h-[500px] overflow-hidden animate-fade-in">
                    {activeTab === "3d" && (
                      <iframe 
                        id="viewer" 
                        className="w-full h-full border-0 min-h-[400px] md:min-h-[500px]" 
                        width="100%"
                        height="500"
                        allow="fullscreen; xr-spatial-tracking" 
                        src="https://superspl.at/s?id=91c1e47e"
                      />
                    )}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      {/* Fund Wallet Modal - Onramp */}
      <FundWalletModal
        isOpen={showFundModal}
        onClose={() => setShowFundModal(false)}
        destinationAddress={userWalletAddress || undefined}
        defaultAmount={investmentAmount}
        projectName={project.name}
        onSuccess={() => {
          setShowFundModal(false);
          console.log("🔥 [DEBUG] PAGE.TSX: Modal closed! Funding successful for project:", project.name);
          console.log("🔥 [DEBUG] PAGE.TSX: Triggering handleInvestWithSmartWallet() NOW...");
          handleInvestWithSmartWallet();
        }}
      />
      {/* KYC Required Modal */}
      <OnboardingRequiredModal
        isOpen={showKycModal}
        onClose={() => setShowKycModal(false)}
        onComplete={() => {
            setShowKycModal(false);
        }}
      />
      {/* Operation Agreement Modal */}
      <OperationAgreementModal
        isOpen={showAgreementModal}
        onClose={() => {
          setShowAgreementModal(false);
          setPendingAction(null);
        }}
        onConfirm={handleAgreementConfirm}
        projectName={project.name}
        projectSymbol={project.tokenDetails?.symbol || "Tokens"}
        investmentAmount={investmentAmount}
        expectedYield={project.expectedReturn || "0%"}
        userName={session?.user?.name}
        userEmail={session?.user?.email}
      />
      {/* Placeholder for transactionState and handleInvestWithSmartWallet */}
      {/* In a real component, these would be defined using useState and useCallback/other functions */}
      {/* For example: */}
      {/* const [transactionState, setTransactionState] = useState({ status: "idle", txHash: null, error: null }); */}
      {/* const handleInvestWithSmartWallet = async () => { */}
      {/*   setTransactionState({ status: "pending", txHash: null, error: null }); */}
      {/*   try { */}
      {/*     // Simulate smart wallet investment */}
      {/*     const txHash = await simulateSmartWalletInvestment(investmentAmount); // Use investmentAmount here */}
      {/*     setTransactionState({ status: "success", txHash, error: null }); */}
      {/*   } catch (error) { */}
      {/*     setTransactionState({ status: "error", txHash: null, error: error.message }); */}
      {/*   } */}
      {/* }; */}
      <TransactionStatusModal
          isOpen={transactionState.status !== "idle"}
          onClose={() => setTransactionState({ ...transactionState, status: "idle" })}
          status={transactionState.status}
          amount={confirmedInvestmentAmount}
          acquiredAmount={project.tokenPrice && project.tokenPrice > 0
            ? confirmedInvestmentAmount / project.tokenPrice
            : 0}
          acquiredSymbol={project.tokenDetails?.symbol || "Tokens"}
          txHash={transactionState.txHash}
          error={transactionState.error}
          step={transactionState.step}
          explorerUrl={`${blockchain.blockExplorer.replace(/\/$/, '')}/tx/`}
        />
    </div>
  );
}

export const Route = createFileRoute('/dashboard/user/projects/$id')({
  component: ProjectDetailPage,
})
